import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { CardBox, Badge, Table } from './UI';
import { IconLink } from '@tabler/icons-react';
import { useTheme } from '../context/ThemeContext';

const SourcesDashboard = ({ searchSources = [], sourceCategories = {}, viewMode = 'simple', isGlobal = false }) => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const [disabledCategories, setDisabledCategories] = useState(new Set());

  // Get engine icon helper
  const getEngineIcon = (engine) => {
    const engineName = engine.toLowerCase();
    const logoMap = {
      'openai': '/logos/chatgpt.png',
      'chatgpt': '/logos/chatgpt.png',
      'gemini': '/logos/gemini.png',
      'perplexity': '/logos/perplexity.png'
    };

    const logoSrc = logoMap[engineName];
    if (logoSrc) {
      return (
        <img
          src={logoSrc}
          alt={`${engine} logo`}
          className="w-5 h-5 rounded-full object-cover"
        />
      );
    }
    return <div className="w-5 h-5 rounded-full bg-gray-300"></div>;
  };

  // Colors for category pie chart
  const CATEGORY_COLORS = {
    'Competitor': '#8b5cf6',
    'Ranking': '#10b981',
    'Comparison': '#f59e0b',
    'UserReviews': '#ef4444',
    'BlogPost': '#3b82f6',
    'News': '#06b6d4',
    'Announcements': '#ec4899',
    'Podcasts': '#84cc16',
    'SocialMedia': '#f97316',
    'Wikipedia': '#8b5cf6',
    'Reddit': '#ff4500',
    'Unknown': '#6b7280'
  };

  // Process sources for aggregation
  const processedData = useMemo(() => {
    const domainMap = new Map();
    const engineCounts = {};
    const categoryMap = new Map();

    searchSources.forEach(source => {
      // Aggregate by domain
      if (source.domain) {
        if (domainMap.has(source.domain)) {
          const existing = domainMap.get(source.domain);
          existing.count++;
          existing.engines.add(source.engine);
          existing.categories.add(source.category || 'Unknown');
        } else {
          domainMap.set(source.domain, {
            domain: source.domain,
            count: 1,
            engines: new Set([source.engine]),
            categories: new Set([source.category || 'Unknown'])
          });
        }
      }

      // Count by engine
      const engine = source.engine || 'Unknown';
      engineCounts[engine] = (engineCounts[engine] || 0) + 1;

      // Count by category
      const category = source.category || 'Unknown';
      if (categoryMap.has(category)) {
        categoryMap.get(category).count++;
        categoryMap.get(category).domains.add(source.domain);
      } else {
        categoryMap.set(category, {
          name: category,
          count: 1,
          domains: new Set([source.domain])
        });
      }
    });

    // Convert to arrays and sort
    const topDomains = Array.from(domainMap.values())
      .map(d => ({
        ...d,
        engines: Array.from(d.engines),
        categories: Array.from(d.categories)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, viewMode === 'detailed' ? 20 : 10);

    const categoryData = Array.from(categoryMap.values())
      .map(c => ({
        ...c,
        domains: c.domains.size
      }))
      .sort((a, b) => b.count - a.count);

    return {
      topDomains,
      engineCounts,
      categoryData,
      totalSources: searchSources.length,
      uniqueDomains: domainMap.size
    };
  }, [searchSources, viewMode]);

  // Category translations mapping
  const getCategoryTranslation = (category) => {
    const translationKey = `dashboard.categories.${category}`;
    const translated = t(translationKey);
    // If translation doesn't exist, return original
    return translated === translationKey ? category : translated;
  };

  // Prepare chart data from sourceCategories or processedData
  const chartData = useMemo(() => {
    let data;
    // If we have sourceCategories from backend (global view), use that
    if (sourceCategories && Object.keys(sourceCategories).length > 0) {
      data = Object.entries(sourceCategories)
        .filter(([name]) => name !== 'Unknown') // Filter out Unknown category
        .map(([name, stats]) => ({
          name,
          displayName: getCategoryTranslation(name),
          value: stats.count,
          domains: stats.domains
        }))
        .sort((a, b) => b.value - a.value);
    } else {
      // Otherwise use locally processed data
      data = processedData.categoryData
        .filter(c => c.name !== 'Unknown') // Filter out Unknown category
        .map(c => ({
          name: c.name,
          displayName: getCategoryTranslation(c.name),
          value: c.count,
          domains: c.domains
        }));
    }
    return data;
  }, [sourceCategories, processedData.categoryData, t]);

  // Filter chart data based on disabled categories
  const filteredChartData = useMemo(() => {
    return chartData.filter(item => !disabledCategories.has(item.name));
  }, [chartData, disabledCategories]);

  // Handle category click to toggle enable/disable
  const handleCategoryClick = (data) => {
    // Handle both direct data (from pie click) and legend click
    let categoryName = data?.name || data;

    // Find the original category name from displayName if needed
    const category = chartData.find(item =>
      item.displayName === categoryName || item.name === categoryName
    );

    if (category) {
      setDisabledCategories(prev => {
        const newSet = new Set(prev);
        if (newSet.has(category.name)) {
          newSet.delete(category.name);
        } else {
          newSet.add(category.name);
        }
        return newSet;
      });
    }
  };

  // Prepare bar chart data for engines
  const engineChartData = useMemo(() => {
    return Object.entries(processedData.engineCounts)
      .map(([engine, count]) => ({
        engine: engine === 'openai' ? 'ChatGPT' : engine.charAt(0).toUpperCase() + engine.slice(1),
        count
      }))
      .sort((a, b) => b.count - a.count);
  }, [processedData.engineCounts]);

  if (searchSources.length === 0 && (!sourceCategories || Object.keys(sourceCategories).length === 0)) {
    return (
      <CardBox className="text-center">
        <IconLink size={48} className="mx-auto text-gray-400 dark:text-[#999999] mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-[#f5f5f5] mb-2">{t('dashboard.sources.noDataTitle')}</h3>
        <p className="text-gray-500 dark:text-[#999999]">
          {isGlobal
            ? t('dashboard.sources.noDataMessage')
            : t('dashboard.sources.noDataBatchMessage')}
        </p>
      </CardBox>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Summary Stats */}
      <CardBox
        title={
          <div className="flex items-center gap-2">
            <IconLink size={20} className="text-blue-600 dark:text-blue-300" />
            {isGlobal ? t('dashboard.sources.globalTitle') : t('dashboard.sources.title')}
          </div>
        }
        subtitle={viewMode === 'detailed' ? t('dashboard.sources.comprehensiveBreakdown') : undefined}
      >

        {/* Summary Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 dark:bg-[#262626] rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">{t('dashboard.sources.totalSources')}</div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-300 mt-1">
              {processedData.totalSources}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-[#262626] rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">{t('dashboard.sources.uniqueDomains')}</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">
              {processedData.uniqueDomains}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-[#262626] rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">{t('dashboard.sources.categories')}</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {chartData.length}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-[#262626] rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-[#c4c4c4]">{t('dashboard.sources.engines')}</div>
            <div className="text-2xl font-bold text-orange-600 mt-1">
              {Object.keys(processedData.engineCounts).length}
            </div>
          </div>
        </div>
      </CardBox>

      {/* Charts and Domains Section - Side by Side */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Category Distribution Pie Chart - Improved */}
        <CardBox
          title={t('dashboard.sources.sourceCategoriesDistribution')}
          subtitle={t('dashboard.overview.metrics.clickToToggle')}
        >
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={filteredChartData.length > 0 ? filteredChartData : chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ percent }) => percent > 0.03 ? `${(percent * 100).toFixed(0)}%` : ''}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="displayName"
                  onClick={handleCategoryClick}
                  style={{ cursor: 'pointer' }}
                >
                  {(filteredChartData.length > 0 ? filteredChartData : chartData).map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={disabledCategories.has(entry.name) ? '#e5e7eb' : (CATEGORY_COLORS[entry.name] || '#6b7280')}
                      style={{ opacity: disabledCategories.has(entry.name) ? 0.3 : 1 }}
                    />
                  ))}
                </Pie>
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: isDark ? '#1f1f1f' : '#ffffff',
                    border: isDark ? '1px solid #2e2e2e' : '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '12px',
                    color: isDark ? '#f5f5f5' : undefined,
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  onClick={handleCategoryClick}
                  wrapperStyle={{ cursor: 'pointer', fontSize: '12px', paddingTop: '20px' }}
                  formatter={(value) => {
                    // Find the original name for this displayName
                    const category = chartData.find(item => item.displayName === value);
                    const isDisabled = category && disabledCategories.has(category.name);

                    return (
                      <span style={{
                        textDecoration: isDisabled ? 'line-through' : 'none',
                        opacity: isDisabled ? 0.5 : 1
                      }}>
                        {value}
                      </span>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </CardBox>

        {/* Top Domains List - Moved here to be side by side */}
        <CardBox title={t('dashboard.sources.topReferencedDomains')}>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {processedData.topDomains.map((domain, idx) => (
              <div key={domain.domain} className="flex items-center justify-between bg-gray-50 dark:bg-[#262626] rounded-lg p-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-500 dark:text-[#999999]">#{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-[#f5f5f5] truncate">{domain.domain}</div>
                    {viewMode === 'detailed' && (
                      <div className="text-xs text-gray-500 dark:text-[#999999] mt-1">
                        <span className="inline-flex items-center gap-1">
                          {domain.engines.map(engine => (
                            <span key={engine}>{getEngineIcon(engine)}</span>
                          ))}
                        </span>
                        <span className="ml-2">
                          {domain.categories.map(cat => getCategoryTranslation(cat)).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right ml-2">
                  <div className="text-lg font-semibold text-gray-900 dark:text-[#f5f5f5]">{domain.count}</div>
                  <div className="text-xs text-gray-500 dark:text-[#999999]">{t('dashboard.sources.citations')}</div>
                </div>
              </div>
            ))}
          </div>
        </CardBox>
      </div>

      {/* Engine Distribution Bar Chart - Separate Section */}
      {viewMode === 'detailed' && (
        <CardBox title={t('dashboard.sources.sourcesByEngine')} className="mt-6">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={engineChartData} margin={{ top: 20, right: 40, left: 40, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.1)'} />
                  <XAxis
                    dataKey="engine"
                    tick={{ fontSize: 12, fill: isDark ? '#999999' : '#6b7280' }}
                    angle={0}
                    textAnchor="middle"
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: isDark ? '#999999' : '#6b7280' }}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.95)',
                      border: isDark ? '1px solid #2e2e2e' : '1px solid rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      color: isDark ? '#f5f5f5' : undefined,
                    }}
                    formatter={(value) => [`${value} ${t('dashboard.sources.citations')}`, t('dashboard.sources.count')]}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]} barSize={80} />
                </BarChart>
              </ResponsiveContainer>
            </div>
        </CardBox>
      )}

      {/* Category Details Table - Only in detailed mode */}
      {viewMode === 'detailed' && (
        <CardBox title={t('dashboard.sources.categoryBreakdown')}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-[#2e2e2e]">
              <thead className="bg-gray-50 dark:bg-[#262626]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#999999] uppercase tracking-wider">
                    {t('dashboard.sources.category')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#999999] uppercase tracking-wider">
                    {t('dashboard.sources.citations')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#999999] uppercase tracking-wider">
                    {t('dashboard.sources.uniqueDomainsCount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#999999] uppercase tracking-wider">
                    {t('dashboard.sources.percentage')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-[#1f1f1f] divide-y divide-gray-200 dark:divide-[#2e2e2e]">
                {chartData.map((category) => (
                  <tr key={category.name}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-3"
                          style={{ backgroundColor: CATEGORY_COLORS[category.name] || CATEGORY_COLORS.Unknown }}
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-[#f5f5f5]">{category.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-[#f5f5f5]">
                      {category.value}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-[#f5f5f5]">
                      {category.domains}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-[#f5f5f5]">
                      {((category.value / processedData.totalSources) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBox>
      )}
    </div>
  );
};

export default SourcesDashboard;